import Vector from "victor";
import Rect from "@/wbcontroller/types/Rect";
import KefirBus from "./KefirBus";
import * as Kefir from "kefir";

const voidNoop = () => {
  return;
};

/**
 * Represents all transforms applied to the viewport and contains helper
 * methods to convert between screen and world coordinate spaces
 */
export default class Camera {
  allChanges: Kefir.Stream<void, void>;

  private _viewportSize: Vector;
  private viewportSizeBus = new KefirBus<Vector, void>(
    "Camera.viewportSizeStream"
  );

  private _origin = new Vector(0, 0);
  private originBus = new KefirBus<Vector, void>("Camera.originStream");

  private _scale = 1;
  private scaleBus = new KefirBus<number, void>("Camera.scaleStream");

  constructor(viewportSize: Vector) {
    this._viewportSize = viewportSize;
    this.allChanges = Kefir.merge([
      this.scaleBus.stream.map(voidNoop),
      this.originBus.stream.map(voidNoop),
      this.viewportSizeBus.stream.map(voidNoop),
    ]);
  }

  panAndZoomToImageBounds(el: HTMLImageElement) {
    this.setScale(
      Math.min(
        this._viewportSize.x / el.width,
        this._viewportSize.y / el.height
      )
    );
    this.setOrigin(new Vector(0, 0));
  }

  get scaleStream() {
    return this.scaleBus.stream;
  }

  get scale(): number {
    return this._scale;
  }

  setScale(newScale: number) {
    this._scale = newScale;
    this.scaleBus.emit(newScale);
  }

  get originStream() {
    return this.originBus.stream;
  }

  get origin(): Vector {
    return this._origin;
  }

  setOrigin(newOrigin: Vector) {
    this._origin = newOrigin;
    this.originBus.emit(newOrigin);
  }

  get viewportSizeStream() {
    return this.viewportSizeBus.stream;
  }

  get viewportSize(): Vector {
    return this._viewportSize;
  }

  setViewportSize(s: Vector) {
    this._viewportSize = s;
    this.viewportSizeBus.emit(s);
  }

  get viewportRect(): Rect {
    return new Rect(
      this.convertScreenToWorld(new Vector(0, 0)),
      this.convertScreenToWorld(this.viewportSize)
    );
  }

  transformString(): string {
    return (
      `translate3d(${this.halfViewportSize().x}px, ${
        this.halfViewportSize().y
      }px, 0)` +
      ` scale3d(${this.scale}, ${this.scale}, 1)` +
      ` translate3d(${-this.origin.x}px, ${-this.origin.y}px, 0)`
    );
  }

  scaleVector(): Vector {
    return new Vector(this.scale, this.scale);
  }

  halfViewportSize(): Vector {
    return this.viewportSize.clone().multiply(new Vector(0.5, 0.5));
  }

  applyTransform(ctx: CanvasRenderingContext2D) {
    const hvps = this.halfViewportSize();
    ctx.translate(hvps.x, hvps.y);
    ctx.scale(this.scale, this.scale);
    ctx.translate(-this.origin.x, -this.origin.y);
  }

  convertScreenToWorld(pointFromScreenUpperLeft: Vector): Vector {
    const pointFromScreenCenter = pointFromScreenUpperLeft
      .clone()
      .subtract(this.halfViewportSize());
    const pointFromWorldCenter = pointFromScreenCenter
      .clone()
      .divide(this.scaleVector());
    const pointInWorld = pointFromWorldCenter.clone().add(this.origin);
    return pointInWorld;
  }

  convertWorldToScreen(worldPoint: Vector): Vector {
    return worldPoint
      .clone()
      .subtract(this.origin)
      .multiply(this.scaleVector())
      .add(this.halfViewportSize());
  }

  convertWorldToScreenScale(worldPoint: Vector): Vector {
    return worldPoint.clone().multiply(this.scaleVector());
  }
}
